// React Query hooks for the company settings.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getCompanyOverview,
  listCompanySettings,
  updateCompany,
  upsertCompanySetting,
} from '../../../../api/company';
import { queryKeys } from '../../../../api/queryKeys';
import { useToast } from '../../../../hooks/useToast';
import type { CompanyOverviewResponse, UpdateCompanyInput } from '../../../../types/api';

export function useCompanyOverview() {
  return useQuery<CompanyOverviewResponse>({
    queryKey: queryKeys.company.overview,
    queryFn: getCompanyOverview,
    staleTime: 60_000,
  });
}

export function useUpdateCompany() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (input: UpdateCompanyInput) => updateCompany(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.company.all });
      void qc.invalidateQueries({ queryKey: queryKeys.company.theme });
      toast.success('Company settings saved.');
    },
    onError: () => {
      toast.error('Failed to save company settings.');
    },
  });
}

export function useCompanySettings() {
  return useQuery({
    queryKey: queryKeys.company.settings,
    queryFn: listCompanySettings,
    staleTime: 60_000,
  });
}

export function useUpsertCompanySetting() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({
      key,
      value,
      type,
      description,
      is_secret,
    }: {
      key: string;
      value: string | null;
      type?: string;
      description?: string;
      is_secret?: boolean;
    }) => upsertCompanySetting(key, { value, type, description, is_secret }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.company.settings });
      toast.success('Setting saved.');
    },
    onError: () => {
      toast.error('Failed to save setting.');
    },
  });
}
